import { useEffect, useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, MoveUp, MoveDown, RefreshCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Database } from "@/types/supabase";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

type Category = Database['public']['Tables']['categories']['Row'] & {
  projectCount: number;
  order_num: number;
};

export default function PortfolioCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isReordering, setIsReordering] = useState(false);
  const { toast } = useToast();

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get all categories
      const { data, error } = await supabase
        .from('categories')
        .select('id, name_tr, name_en, name_ar, order_num')
        .order('order_num', { ascending: true });
      
      if (error) throw error;
      
      // Get project counts for each category
      const categoriesWithCounts = await Promise.all((data || []).map(async (category) => {
        const { count, error: countError } = await supabase
          .from('projects')
          .select('id', { count: 'exact' })
          .eq('category_id', category.id);
        
        if (countError) throw countError;
        
        return {
          ...category,
          projectCount: count || 0
        };
      }));
      
      setCategories(categoriesWithCounts);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Veri yükleme hatası",
        description: "Kategoriler yüklenirken bir hata oluştu.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [toast]);

  // Sıralama numaralarını sıfırla ve 1'den başlayarak yeniden numaralandır
  const resetOrderNumbers = async () => {
    try {
      setIsReordering(true);
      toast({
        title: "İşlem başladı",
        description: "Kategoriler yeniden sıralanıyor...",
      });

      // Kategorilerin mevcut sırasını koru, sadece sıra numaralarını güncelle
      const updatedCategories = [...categories].map((category, index) => ({
        ...category,
        order_num: index + 1
      }));

      // Veritabanında toplu güncelleme yapmak için
      for (const category of updatedCategories) {
        const { error } = await supabase
          .from('categories')
          .update({ order_num: category.order_num })
          .eq('id', category.id);

        if (error) {
          console.error("Kategori güncelleme hatası:", error);
          throw error;
        }
      }

      // Güncellenmiş kategori listesini ayarla
      setCategories(updatedCategories);

      toast({
        title: "Başarılı",
        description: "Kategoriler başarıyla yeniden sıralandı.",
      });
    } catch (error) {
      console.error("Kategori sıralama sıfırlama hatası:", error);
      toast({
        title: "Hata",
        description: "Kategoriler yeniden sıralanırken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsReordering(false);
    }
  };

  const moveCategory = async (id: string, direction: "up" | "down") => {
    const currentCategories = [...categories];
    const index = currentCategories.findIndex(category => category.id === id);
    
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === currentCategories.length - 1)
    ) {
      return;
    }
    
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    
    // Geçici olarak sıralamayı güncelle
    [currentCategories[index], currentCategories[swapIndex]] = [
      currentCategories[swapIndex], 
      currentCategories[index]
    ];
    
    // Sıralama numaralarını güncelle
    const updatedCategories = currentCategories.map((category, i) => ({
      ...category,
      order_num: i + 1
    }));
    
    setCategories(updatedCategories);
    
    // Veritabanında güncelle
    try {
      // İki kategoriyi de güncelle
      const updates = [
        {
          id: updatedCategories[index].id,
          order_num: updatedCategories[index].order_num
        },
        {
          id: updatedCategories[swapIndex].id,
          order_num: updatedCategories[swapIndex].order_num
        }
      ];
      
      for (const update of updates) {
        const { error } = await supabase
          .from('categories')
          .update({ order_num: update.order_num })
          .eq('id', update.id);
          
        if (error) throw error;
      }
    } catch (error) {
      console.error("Sıralama güncelleme hatası:", error);
      toast({
        title: "Hata",
        description: "Kategori sırası güncellenirken bir sorun oluştu.",
        variant: "destructive",
      });
      // Hatadan sonra verileri yeniden yükle
      fetchData();
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      // Önce bağlı projelerin kategori ID'sini null yap
      const { error: projectsError } = await supabase
        .from('projects')
        .update({ category_id: null })
        .eq('category_id', id);
        
      if (projectsError) throw projectsError;
      
      // Sonra kategoriyi sil
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast({
        title: "Başarılı",
        description: "Kategori başarıyla silindi.",
      });
      
      // Kategoriyi listeden kaldır
      setCategories(categories.filter(category => category.id !== id));
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Hata",
        description: "Kategori silinirken bir sorun oluştu.",
        variant: "destructive",
      });
    }
  };

  return (
    <ContentLayout 
      title="Portföy Kategorileri" 
      backLink="/dashboard"
      actionButton={{
        label: "Yeni Kategori Ekle",
        href: "/portfolio/categories/new"
      }}
    >
      {loading ? (
        <div className="flex justify-center p-8">
          <p>Kategoriler yükleniyor...</p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex justify-end">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-1"
              onClick={resetOrderNumbers}
              disabled={isReordering || categories.length <= 1}
            >
              <RefreshCcw className="h-4 w-4" />
              <span>Sıralamayı Düzelt</span>
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Sıra</TableHead>
                <TableHead>Kategori Adı</TableHead>
                <TableHead>Proje Sayısı</TableHead>
                <TableHead className="w-[200px]">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    Henüz hiç kategori bulunmuyor
                  </TableCell>
                </TableRow>
              ) :
                categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell className="font-medium">{category.order_num}</TableCell>
                    <TableCell className="font-medium">{category.name_tr}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{category.projectCount}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => moveCategory(category.id, "up")}>
                          <MoveUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => moveCategory(category.id, "down")}>
                          <MoveDown className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" asChild>
                          <a href={`/portfolio/categories/${category.id}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </a>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Kategoriyi sil</AlertDialogTitle>
                              <AlertDialogDescription>
                                Bu kategoriyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>İptal</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => deleteCategory(category.id)}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                Sil
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </>
      )}
    </ContentLayout>
  );
}
